import { useEffect, useMemo, useState } from 'react';
import { Tabs, Tab } from '@mui/material';
import { RefreshCw, Search } from 'lucide-react';
import { Button, Chip, Input, FormField, CoordioDropdown, useStaticDropdownSource } from '@coordio/ui';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../components/dialogs/CortexDialog';
import { guardrailService, getGuardrailErrorMessage } from '../services/guardrailService';
import { agentService } from '../services/agentService';
import {
  GuardrailVerdict,
  GuardrailOutcome,
  guardrailActionLabels,
  guardrailDirectionLabels,
  guardrailModeLabels,
  guardrailOutcomeLabels,
  guardrailVerdictLabels,
  type GuardrailExecutionDto,
  type UnguardedTurnDto,
} from '../types/guardrail';
import type { AgentDefinitionDto } from '../types/agent';
import { useNotificationStore } from '../store/notificationStore';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { GuardrailsSectionNav } from '../components/guardrails/GuardrailsSectionNav';
import { PageListLayout } from '../components/layouts/PageListLayout';

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const verdictColor = (verdict: number) => {
  switch (verdict) {
    case GuardrailVerdict.Pass:
      return 'green' as const;
    case GuardrailVerdict.Violation:
      return 'red' as const;
    case GuardrailVerdict.Error:
      return 'amber' as const;
    case GuardrailVerdict.Inconclusive:
      return 'amber' as const;
    default:
      return 'default' as const;
  }
};

const outcomeColor = (outcome: number) => {
  switch (outcome) {
    case GuardrailOutcome.Allowed:
    case GuardrailOutcome.Observed:
      return 'green' as const;
    case GuardrailOutcome.Blocked:
    case GuardrailOutcome.Terminated:
      return 'red' as const;
    case GuardrailOutcome.Modified:
    case GuardrailOutcome.PendingApproval:
    case GuardrailOutcome.Escalated:
      return 'amber' as const;
    default:
      return 'default' as const;
  }
};

const prettyJson = (raw?: string | null): string | null => {
  if (!raw) return null;
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const SINCE_OPTIONS = [
  { value: '1', label: 'Last 24 hours' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

/**
 * Guardrail executions page: unguarded-turn coverage report plus a stage-correlation lookup.
 */
export const GuardrailExecutionsPage = () => {
  const { addNotification } = useNotificationStore();

  const [tab, setTab] = useState(0);
  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);

  // Unguarded turns tab
  const [sinceDays, setSinceDays] = useState(7);
  const [unguarded, setUnguarded] = useState<UnguardedTurnDto[]>([]);
  const [unguardedLoading, setUnguardedLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Stage lookup tab
  const [stageId, setStageId] = useState('');
  const [executions, setExecutions] = useState<GuardrailExecutionDto[]>([]);
  const [stageLoading, setStageLoading] = useState(false);
  const [stageSearched, setStageSearched] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState<GuardrailExecutionDto | null>(null);

  const sinceSource = useStaticDropdownSource(SINCE_OPTIONS);

  const agentNameById = useMemo(() => {
    const map = new Map<string, string>();
    agents.forEach((agent) => map.set(agent.id, agent.name));
    return map;
  }, [agents]);

  const loadUnguarded = async () => {
    setUnguardedLoading(true);
    setError(null);
    try {
      const sinceUtc = new Date(Date.now() - sinceDays * 24 * 60 * 60 * 1000).toISOString();
      const data = await guardrailService.getUnguardedTurns(sinceUtc, 100);
      setUnguarded(data);
    } catch (err: unknown) {
      const message = getGuardrailErrorMessage(err, 'Failed to load unguarded turns');
      setError(message);
      addNotification(message, 'error');
    } finally {
      setUnguardedLoading(false);
    }
  };

  useEffect(() => {
    loadUnguarded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinceDays]);

  useEffect(() => {
    // Agent names are display-only; failures fall back to raw ids.
    agentService
      .getAgents({ pageNumber: 1, pageSize: 200 })
      .then((page) => setAgents(page.items))
      .catch(() => setAgents([]));
  }, []);

  const stageIdValid = GUID_PATTERN.test(stageId.trim());

  const handleStageLookup = async () => {
    if (!stageIdValid) {
      addNotification('Enter a valid StageCorrelationId GUID', 'error');
      return;
    }
    setStageLoading(true);
    setError(null);
    try {
      const data = await guardrailService.getStageExecutions(stageId.trim());
      setExecutions(data);
      setStageSearched(true);
    } catch (err: unknown) {
      const message = getGuardrailErrorMessage(err, 'Failed to load stage executions');
      setError(message);
      addNotification(message, 'error');
    } finally {
      setStageLoading(false);
    }
  };

  const unguardedColumns: DataTableColumn<UnguardedTurnDto>[] = [
    {
      id: 'occurredAtUtc',
      label: 'Occurred At',
      minWidth: 160,
      render: (row) => new Date(row.occurredAtUtc).toLocaleString(),
    },
    {
      id: 'direction',
      label: 'Direction',
      minWidth: 110,
      render: (row) => guardrailDirectionLabels[row.direction] ?? row.direction,
    },
    {
      id: 'agent',
      label: 'Agent',
      minWidth: 140,
      render: (row) =>
        row.agentDefinitionId ? agentNameById.get(row.agentDefinitionId) ?? row.agentDefinitionId : '-',
    },
    {
      id: 'stageCorrelationId',
      label: 'Stage Correlation ID',
      minWidth: 300,
      render: (row) => (
        <span className="text-sm font-mono text-[0.8125rem]">
          {row.stageCorrelationId}
        </span>
      ),
    },
    {
      id: 'agentSessionId',
      label: 'Session',
      minWidth: 140,
      render: (row) =>
        row.agentSessionId ? (
          <span className="text-sm font-mono text-[0.8125rem]">
            {row.agentSessionId.slice(0, 8)}…
          </span>
        ) : (
          '-'
        ),
    },
  ];

  const executionColumns: DataTableColumn<GuardrailExecutionDto>[] = [
    {
      id: 'guardrailKey',
      label: 'Guardrail',
      minWidth: 140,
      render: (row) => (
        <span className="text-sm font-mono text-[0.8125rem]">
          {row.guardrailKey ?? '(unguarded sentinel)'}
          {row.guardrailVersion != null ? ` v${row.guardrailVersion}` : ''}
        </span>
      ),
    },
    {
      id: 'verdict',
      label: 'Verdict',
      minWidth: 110,
      render: (row) => (
        <Chip
          label={guardrailVerdictLabels[row.verdict] ?? row.verdict}
          color={verdictColor(row.verdict)}
          className="text-xs"
        />
      ),
    },
    {
      id: 'outcome',
      label: 'Outcome',
      minWidth: 120,
      render: (row) => (
        <Chip
          label={guardrailOutcomeLabels[row.outcome] ?? row.outcome}
          color={outcomeColor(row.outcome)}
          className="text-xs"
          variant="outlined"
        />
      ),
    },
    {
      id: 'actionTaken',
      label: 'Action',
      minWidth: 110,
      render: (row) => guardrailActionLabels[row.actionTaken] ?? row.actionTaken,
    },
    {
      id: 'mode',
      label: 'Mode',
      minWidth: 90,
      render: (row) => guardrailModeLabels[row.mode] ?? row.mode,
    },
    { id: 'matchCount', label: 'Matches', minWidth: 80, align: 'right' },
    {
      id: 'durationMs',
      label: 'Duration',
      minWidth: 90,
      align: 'right',
      render: (row) => `${row.durationMs} ms`,
    },
    {
      id: 'occurredAtUtc',
      label: 'Occurred At',
      minWidth: 160,
      render: (row) => new Date(row.occurredAtUtc).toLocaleString(),
    },
  ];

  const selectedResultJson = prettyJson(selectedExecution?.resultJson);

  return (
    <>
      <PageListLayout
        title="Guardrail Executions"
        subtitle="Coverage report of unguarded turns, and per-stage evaluation lookup."
        error={error || undefined}
        onErrorDismiss={() => setError(null)}
        headerActions={
          tab === 0 ? (
            <Button
              variant="outline"
              onClick={loadUnguarded}
              disabled={unguardedLoading}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          ) : undefined
        }
      >
        <GuardrailsSectionNav />
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 4 }}>
          <Tab label="Unguarded Turns" />
          <Tab label="Stage Lookup" />
        </Tabs>

        {tab === 0 && (
          <div>
            <div className="flex gap-2 mb-4 items-center">
              <CoordioDropdown
                source={sinceSource}
                value={sinceDays.toString()}
                onChange={(val) => setSinceDays(Number(val))}
                placeholder="Since"
                className="min-w-[160px]"
              />
              <span className="text-xs text-[var(--color-text-secondary)]">
                Turns that ran with the guardrail engine enabled but zero bindings resolved (latest 100).
              </span>
            </div>
            <DataTable
              columns={unguardedColumns}
              rows={unguarded}
              loading={unguardedLoading}
              emptyMessage="No unguarded turns in the selected window — every turn was covered."
            />
          </div>
        )}

        {tab === 1 && (
          <div>
            <div className="flex gap-2 mb-4 items-start">
              <FormField
                label="Stage Correlation ID"
                error={stageId.trim() !== '' && !stageIdValid ? 'Must be a GUID' : undefined}
                className="flex-grow max-w-[420px]"
              >
                <Input
                  value={stageId}
                  onChange={(e) => setStageId(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleStageLookup();
                  }}
                />
              </FormField>
              <Button
                onClick={handleStageLookup}
                disabled={!stageIdValid || stageLoading}
                className="mt-6"
              >
                <Search className="mr-2 h-4 w-4" />
                Lookup
              </Button>
            </div>
            <DataTable
              columns={executionColumns}
              rows={executions}
              loading={stageLoading}
              emptyMessage={
                stageSearched
                  ? 'No execution rows found for this stage correlation id.'
                  : 'Enter a StageCorrelationId GUID to look up its execution rows.'
              }
              onRowClick={(row) => setSelectedExecution(row)}
            />
            {executions.length > 0 && (
              <span className="text-xs text-[var(--color-text-secondary)] block mt-2">
                Click a row to see its result detail.
              </span>
            )}
          </div>
        )}
      </PageListLayout>

      {/* Execution detail dialog */}
      <CortexDialog
        open={!!selectedExecution}
        onClose={() => setSelectedExecution(null)}
        size="lg"
        title={`Execution Detail — ${selectedExecution?.guardrailKey ?? 'unguarded sentinel'}`}
      >
        <CortexDialogBody>
          {selectedExecution && (
            <div className="flex flex-col gap-2 pt-1">
              <div className="flex gap-1 flex-wrap">
                <Chip
                  label={`Verdict: ${guardrailVerdictLabels[selectedExecution.verdict] ?? selectedExecution.verdict}`}
                  color={verdictColor(selectedExecution.verdict)}
                  className="text-xs"
                />
                <Chip
                  label={`Outcome: ${guardrailOutcomeLabels[selectedExecution.outcome] ?? selectedExecution.outcome}`}
                  color={outcomeColor(selectedExecution.outcome)}
                  className="text-xs"
                  variant="outlined"
                />
                <Chip
                  label={`Action: ${guardrailActionLabels[selectedExecution.actionTaken] ?? selectedExecution.actionTaken}${selectedExecution.actionApplied ? '' : ' (not applied)'}`}
                  className="text-xs"
                  variant="outlined"
                />
                <Chip label={`Severity: ${selectedExecution.severity}`} className="text-xs" variant="outlined" />
                {selectedExecution.cacheHit && <Chip label="Cache hit" className="text-xs" variant="outlined" />}
                {selectedExecution.timedOut && <Chip label="Timed out" color="red" className="text-xs" />}
              </div>
              {selectedExecution.errorMessage && (
                <span className="text-sm text-red-600">
                  {selectedExecution.errorCode ? `${selectedExecution.errorCode}: ` : ''}
                  {selectedExecution.errorMessage}
                </span>
              )}
              <h3 className="text-sm font-semibold">Result JSON</h3>
              <pre className="m-0 p-3 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-inset,#fafafa)] font-mono text-[0.8125rem] overflow-auto max-h-[400px] whitespace-pre-wrap break-words">
                {selectedResultJson ?? 'No result detail recorded.'}
              </pre>
            </div>
          )}
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button variant="outline" onClick={() => setSelectedExecution(null)}>Close</Button>
        </CortexDialogFooter>
      </CortexDialog>
    </>
  );
};
